import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import QueryParams from 'utils/queryParams';
import { API_PODS } from 'services/dataService/resources';

import type { PodDetailResponse, PodFilters, PodListResponse } from '../types';

const cleanFilters = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as Partial<T>;

export const usePods = (initialFilters: Partial<PodFilters> = {}) => {
  const [filters, setFilters] = useState<PodFilters>({
    page: 1,
    items: 20,
    ...cleanFilters(initialFilters),
  });

  const updateFilters = useCallback((newFilter: Partial<PodFilters>) => {
    setFilters(
      (prev) => cleanFilters({ ...prev, page: 1, ...newFilter }) as PodFilters,
    );
  }, []);

  const { data, error, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [API_PODS, 'list', filters],
    keepPreviousData: true,
    queryFn: async () => {
      const filtersString = QueryParams.stringify(filters, {
        arrayFormat: 'comma',
      });
      const { error: localError, data: localData } =
        await dataService.getList<PodListResponse>(
          `${API_PODS}?${filtersString}`,
        );
      if (localError) throw new Error(localError);
      return localData;
    },
  });

  // Auto-navigate to previous page when current page becomes empty after deletion
  useEffect(() => {
    if (
      data &&
      data.data.length === 0 &&
      data.pagination.totalCount > 0 &&
      (filters.page ?? 1) > 1
    ) {
      const lastValidPage = Math.max(1, data.pagination.totalPages);
      setFilters((prev) => ({ ...prev, page: lastValidPage }));
    }
  }, [data, filters.page]);

  return {
    pods: data?.data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    pagination: data?.pagination,
    updateFilters,
    filters,
  };
};

export const useGetPod = (guid: string, options?: { enabled?: boolean }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [API_PODS, 'detail', guid],
    enabled: !!guid && (options?.enabled ?? true),
    queryFn: async () => {
      const { error: localError, data: localData } =
        await dataService.getOne<PodDetailResponse>(API_PODS, guid);
      if (localError) throw new Error(localError);
      return localData;
    },
  });

  return {
    pod: data?.pod,
    clinics: data?.clinics,
    members: data?.members,
    capacity: data?.capacity,
    isLoading,
    isError,
  };
};
